import {
  MAX_RETURN_PHOTOS_PER_LINE,
  MAX_RETURN_PHOTO_BYTES,
} from "@estelle/domain";

/**
 * Whether a file chosen for a damaged item can be a photo, checked the moment it
 * is chosen (#558). The server checks again, from the file's own bytes, and is
 * the authority; this is so a person is told *why* a file was not added instead
 * of finding a broken thumbnail and a refusal only after pressing Confirm.
 *
 * The browser's own type is what is read here, with the extension as the
 * fallback some phones need (a file with no type at all). HEIC gets its own
 * words: an iPhone's gallery can hand one over, and "not an image" would be
 * false.
 */

const TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"] as const;
const EXTENSIONS = [".jpg", ".jpeg", ".png", ".webp", ".gif"] as const;

const ONLY = "photos must be JPEG, PNG, WebP or GIF images";

function megabytes(bytes: number): string {
  return (bytes / 1024 / 1024).toFixed(1);
}

/** Why the file cannot be added, in words, or `null` when it can. */
export function photoProblem(file: {
  name: string;
  type: string;
  size: number;
}): string | null {
  const name = file.name === "" ? "This file" : `"${file.name}"`;
  const lower = file.name.toLowerCase();

  if (/\.(heic|heif)$/.test(lower) || /hei[cf]/.test(file.type)) {
    return `${name} was not added: HEIC photos are not accepted. Take the photo with Take photo, or save it as JPEG first.`;
  }
  const typed = (TYPES as readonly string[]).includes(file.type);
  const named = EXTENSIONS.some((extension) => lower.endsWith(extension));
  if (!(file.type === "" ? named : typed)) {
    return `${name} was not added: ${ONLY}.`;
  }
  if (file.size === 0) return `${name} was not added: the file is empty.`;
  if (file.size > MAX_RETURN_PHOTO_BYTES) {
    return `${name} was not added: it is ${megabytes(file.size)} MB and a photo can be at most ${megabytes(MAX_RETURN_PHOTO_BYTES)} MB.`;
  }
  return null;
}

/**
 * What a pick adds to a line's photos: the files that pass, in order, up to the
 * most a line holds, and a sentence for every one that did not.
 */
export function addPhotos(
  current: readonly File[],
  picked: readonly File[],
): { files: File[]; notices: string[] } {
  const files = [...current];
  const notices: string[] = [];
  for (const file of picked) {
    const problem = photoProblem(file);
    if (problem !== null) {
      notices.push(problem);
    } else if (files.length >= MAX_RETURN_PHOTOS_PER_LINE) {
      notices.push(
        `"${file.name}" was not added: a line holds at most ${String(MAX_RETURN_PHOTOS_PER_LINE)} photos.`,
      );
    } else {
      files.push(file);
    }
  }
  return { files, notices };
}
